import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Loader2, Trash2, Pencil, Receipt, Filter, RefreshCw, X,
  Upload, FileDown, Sparkles,
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr, notifyErr, inputCls, todayISO } from '../../../components/admin/billing/shared';
import { DriveIQBadge } from '../../../components/admin/DriveIQ';
import { BillThumb, BillViewer } from '../../../components/admin/expenses/BillViewer';

const METHODS = ['Cash', 'UPI', 'NEFT', 'IMPS', 'RTGS', 'Cheque', 'Card', 'Bank', 'Other'];
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z][Z][0-9A-Z]$/;

const REVENUE_HEADS = ['Detailing', 'Body Shop', 'Mechanical', 'Accessories', 'Consumables', 'Overhead'];

const EMPTY = {
  date: todayISO(), category: '', amount: 0, paid_to: '',
  method: 'Cash', reference: '', description: '', attachment_url: '',
  linked_invoice_id: null, linked_staff_id: null,
  is_gst_invoice: false, supplier_gstin: '',
  taxable_value: 0, cgst_amount: 0, sgst_amount: 0,
  igst_amount: 0, cess_amount: 0,
  revenue_head: '', revenue_head_auto: false,
};

const ExpensesPage = () => {
  const { authHeader, user } = useAuth();
  const isMaster = !!user?.is_master_admin;
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [editing, setEditing] = useState(null); // expense doc or EMPTY
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [viewingBill, setViewingBill] = useState('');
  const [autofillHint, setAutofillHint] = useState('');
  const [dupeMatches, setDupeMatches] = useState([]);
  const [dupeMatchAmount, setDupeMatchAmount] = useState(false);
  const supplierLookupTimer = useRef(null);
  const dupeCheckTimer = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterFrom) params.append('date_from', filterFrom);
      if (filterTo) params.append('date_to', filterTo);
      if (filterCat) params.append('category', filterCat);
      const [{ data: expData }, { data: catData }] = await Promise.all([
        axios.get(`${API}/expenses?${params.toString()}`, { headers: authHeader() }),
        axios.get(`${API}/expense-categories`, { headers: authHeader() }),
      ]);
      setList(expData || []);
      setCats(catData || []);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [authHeader, filterFrom, filterTo, filterCat]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Debounced duplicate-bill check. Runs whenever the user changes the
  // reference/paid_to/GSTIN/amount inside the editor (amount only when the
  // "Also match amount" toggle is on). Non-blocking — the banner is purely
  // informational (the user can still save if they intend to).
  useEffect(() => {
    if (!editing) {
      setDupeMatches([]);
      return undefined;
    }
    const ref = (editing.reference || '').trim();
    const gstin = (editing.supplier_gstin || '').trim();
    const name = (editing.paid_to || '').trim();
    if (!ref || (!gstin && !name)) {
      setDupeMatches([]);
      return undefined;
    }
    if (dupeCheckTimer.current) clearTimeout(dupeCheckTimer.current);
    dupeCheckTimer.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ reference: ref });
        if (gstin) params.set('supplier_gstin', gstin);
        if (name) params.set('supplier_name', name);
        if (dupeMatchAmount && Number(editing.amount) > 0) {
          params.set('amount', String(Number(editing.amount)));
        }
        if (editing.id) params.set('exclude_id', editing.id);
        const { data } = await axios.get(
          `${API}/expenses/duplicate-check?${params.toString()}`,
          { headers: authHeader() },
        );
        setDupeMatches(Array.isArray(data?.matches) ? data.matches : []);
      } catch (e) {
        // silent — non-critical enhancement
      }
    }, 400);
    return () => {
      if (dupeCheckTimer.current) clearTimeout(dupeCheckTimer.current);
    };
  }, [editing?.reference, editing?.supplier_gstin, editing?.paid_to, editing?.amount, editing?.id, dupeMatchAmount, authHeader]);

  const total = useMemo(() => list.reduce((a, x) => a + Number(x.amount || 0), 0), [list]);

  const save = async () => {
    if (!editing.category || !editing.amount) {
      setErr('Category and amount are required.');
      return;
    }
    setSaving(true); setErr('');
    try {
      const body = { ...editing };
      if (editing.id) {
        await axios.put(`${API}/expenses/${editing.id}`, body, { headers: authHeader() });
      } else {
        await axios.post(`${API}/expenses`, body, { headers: authHeader() });
      }
      setEditing(null);
      await fetchAll();
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/expenses/${id}`, { headers: authHeader() });
      await fetchAll();
    } catch (e) { notifyErr(e); }
  };

  const uploadAttachment = async (file) => {
    // Dedicated expense-attachment endpoint. (Previously piggybacked on
    // /inspections/{tmpId}/media with a fabricated id — that endpoint
    // required a real inspection row so every upload 404'd.)
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setErr('');
    let uploadedUrl = '';
    try {
      const { data } = await axios.post(
        `${API}/expenses/attachment`,
        fd,
        { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } },
      );
      uploadedUrl = data?.secure_url || data?.url || '';
      if (!uploadedUrl) {
        setErr('Upload succeeded but no URL was returned.');
        return;
      }
      setEditing((p) => ({ ...p, attachment_url: uploadedUrl }));
    } catch (e) {
      setErr(formatErr(e));
      return;
    }

    // Auto-OCR: images and PDFs are both supported server-side (PDF is
    // rendered to JPEG on the backend via PyMuPDF). Everything else skip.
    const okType = (file.type || '').startsWith('image/')
      || file.type === 'application/pdf'
      || file.name?.toLowerCase().endsWith('.pdf');
    if (!okType) return;
    setExtracting(true);
    try {
      const { data: ex } = await axios.post(
        `${API}/expenses/attachment/extract`,
        { attachment_url: uploadedUrl },
        { headers: authHeader() },
      );
      setEditing((p) => {
        if (!p) return p;
        // Never overwrite what the user has already typed (manual override wins).
        const patch = { ...p };
        if (!p.paid_to && ex?.supplier_name) patch.paid_to = ex.supplier_name;
        if (!p.supplier_gstin && ex?.supplier_gstin) patch.supplier_gstin = ex.supplier_gstin;
        if (!p.reference && ex?.bill_number) patch.reference = ex.bill_number;
        // Invoice date — only overwrite if the field is still today's default.
        // (Editing an existing expense already has its saved date, so we
        // never touch it there.)
        if (ex?.invoice_date && p.date === todayISO()) {
          patch.date = ex.invoice_date;
        }
        // Category — only auto-select if the user hasn't picked one yet.
        if (!p.category && ex?.category) patch.category = ex.category;
        // Only auto-flip the checkbox on. Never turn a user's manual "true"
        // back into false.
        if (ex?.is_gst_invoice) patch.is_gst_invoice = true;
        // Tax breakup — only fill fields the user hasn't already set to a
        // non-zero value. Manual override wins.
        if (!Number(p.taxable_value) && Number(ex?.taxable_value)) patch.taxable_value = Number(ex.taxable_value);
        if (!Number(p.cgst_amount) && Number(ex?.cgst_amount)) patch.cgst_amount = Number(ex.cgst_amount);
        if (!Number(p.sgst_amount) && Number(ex?.sgst_amount)) patch.sgst_amount = Number(ex.sgst_amount);
        if (!Number(p.igst_amount) && Number(ex?.igst_amount)) patch.igst_amount = Number(ex.igst_amount);
        if (!Number(p.cess_amount) && Number(ex?.cess_amount)) patch.cess_amount = Number(ex.cess_amount);
        // Grand-total (Amount) — the final payable value including tax.
        // Only auto-fill when the user hasn't already typed an amount.
        if (!Number(p.amount) && Number(ex?.grand_total)) patch.amount = Number(ex.grand_total);
        return patch;
      });
      if (ex?.supplier_name || ex?.supplier_gstin || ex?.bill_number
          || ex?.invoice_date || ex?.category
          || Number(ex?.taxable_value) || Number(ex?.cgst_amount)
          || Number(ex?.grand_total)) {
        const pageStr = (Number(ex?.pages_processed) || 0) > 1
          ? ` (${ex.pages_processed} pages)`
          : '';
        setAutofillHint(`Auto-filled from invoice${pageStr} ✓`);
        setTimeout(() => setAutofillHint(''), 4000);
      }
    } catch (e) {
      // Non-fatal — the file uploaded fine, just no OCR. Keep the form open.
      console.warn('[expenses] OCR failed', e);
    } finally {
      setExtracting(false);
    }
  };

  // Debounced supplier-name lookup: while the user types "Paid To", if we
  // have a cached supplier record (from a prior bill), fill GSTIN + tick the
  // GST checkbox instantly — no need to wait for OCR on the next upload.
  const onPaidToChange = (value) => {
    setEditing((p) => ({ ...p, paid_to: value }));
    if (supplierLookupTimer.current) {
      clearTimeout(supplierLookupTimer.current);
    }
    const q = (value || '').trim();
    if (q.length < 2) return;
    supplierLookupTimer.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(
          `${API}/expenses/suppliers/lookup?name=${encodeURIComponent(q)}`,
          { headers: authHeader() },
        );
        if (!data?.match) return;
        setEditing((p) => {
          if (!p) return p;
          const patch = { ...p };
          if (!p.supplier_gstin && data.match.gstin) {
            patch.supplier_gstin = data.match.gstin;
            patch.is_gst_invoice = patch.is_gst_invoice || !!data.match.gstin;
          }
          // Category memory — remembered from the last expense saved for
          // this supplier. Only fills if the user hasn't picked one yet.
          if (!p.category && data.match.last_category) {
            patch.category = data.match.last_category;
          }
          if (!p.revenue_head && data.match.last_revenue_head) {
            patch.revenue_head = data.match.last_revenue_head;
            patch.revenue_head_auto = false; // supplier memory = human-confirmed
          }
          return patch;
        });
      } catch (e) {
        // silent — non-critical enhancement
      }
    }, 400);
  };

  const exportCSV = () => {
    // Master's CSV includes the "Entered by" column so the accountant can
    // reconcile who filed what. Non-masters only ever see their own rows
    // so the column would be constant noise for them.
    const header = ['Date', 'Category', 'Revenue Head', 'Amount', 'Paid To', 'Method', 'Description', 'GST Invoice', 'Supplier GSTIN'];
    if (isMaster) header.push('Entered By');
    const rows = list.map((x) => {
      const row = [
        x.date, x.category, x.revenue_head || '', x.amount, x.paid_to || '', x.method || '',
        (x.description || '').replace(/[\n,]/g, ' '),
        x.is_gst_invoice ? 'Yes' : 'No',
        x.supplier_gstin || '',
      ];
      if (isMaster) row.push(x.created_by || 'system');
      return row;
    });
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `expenses_${todayISO()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4 p-4 sm:p-6 bg-slate-50 min-h-full" data-testid="expenses-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Receipt className="text-teal-600" size={22} />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Expenses</h1>
          <span className="ml-2 text-sm text-slate-500">
            {list.length} entries · {fmtINR(total)}
          </span>
          {!isMaster && (
            <span
              className="ml-2 inline-flex items-center text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200 rounded-full px-2 py-0.5"
              title="You're seeing only the expenses you entered. Master admin sees everything."
              data-testid="own-expenses-badge"
            >
              Your entries only
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {isMaster && (
            <>
              <button onClick={() => navigate('/admin/expenses/team')} className="text-sm bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-3 py-2 rounded-lg" data-testid="team-view-btn">
                Team view
              </button>
              <button onClick={() => navigate('/admin/expenses/recurring')} className="text-sm bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-3 py-2 rounded-lg" data-testid="recurring-btn">
                Recurring
              </button>
            </>
          )}
          <button onClick={exportCSV} className="text-sm bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="export-csv-btn">
            <FileDown size={14} /> Export
          </button>
          <button onClick={() => setEditing({ ...EMPTY })} className="text-sm bg-teal-500 hover:bg-teal-600 text-white font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="new-expense-btn">
            <Plus size={14} /> New Expense
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-slate-500 mb-1">From</label>
          <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className={inputCls} data-testid="filter-from" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">To</label>
          <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className={inputCls} data-testid="filter-to" />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Category</label>
          <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className={inputCls} data-testid="filter-category">
            <option value="">All categories</option>
            {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        <button onClick={fetchAll} className="text-sm bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="apply-filters">
          <Filter size={14} /> Apply
        </button>
        {(filterFrom || filterTo || filterCat) && (
          <button onClick={() => { setFilterFrom(''); setFilterTo(''); setFilterCat(''); }} className="text-sm text-slate-500 hover:text-slate-800">
            Clear
          </button>
        )}
      </div>

      {err && !editing && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="page-error">{err}</div>}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">No expenses yet for this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold text-slate-600">Date</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Category</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Paid To</th>
                  <th className="px-3 py-2 font-semibold text-slate-600" data-testid="col-bill">Bill</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Method</th>
                  <th className="px-3 py-2 font-semibold text-slate-600">Description</th>
                  {isMaster && (
                    <th className="px-3 py-2 font-semibold text-slate-600 whitespace-nowrap" data-testid="col-entered-by">Entered by</th>
                  )}
                  <th className="px-3 py-2 font-semibold text-slate-600 text-right">Amount</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((x) => {
                  const enteredBy = (x.created_by || '').trim();
                  const enteredByShort = enteredBy ? enteredBy.split('@')[0] : '';
                  return (
                  <tr key={x.id} className="hover:bg-slate-50" data-testid={`expense-row-${x.id}`}>
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{x.date}</td>
                    <td className="px-3 py-2 text-slate-800">{x.category}</td>
                    <td className="px-3 py-2 text-slate-700">
                      <div className="flex items-center gap-1.5">
                        <span>{x.paid_to || '—'}</span>
                        {x.is_gst_invoice && (
                          <span
                            className="inline-flex items-center bg-teal-50 border border-teal-200 text-teal-700 text-[10px] font-semibold px-1.5 rounded"
                            title={x.supplier_gstin ? `GSTIN: ${x.supplier_gstin}` : 'Marked as GST invoice'}
                            data-testid={`gst-badge-${x.id}`}
                          >
                            GST
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {x.attachment_url ? (
                        <BillThumb
                          url={x.attachment_url}
                          onOpen={setViewingBill}
                          testId={`bill-thumb-${x.id}`}
                        />
                      ) : (
                        <span className="text-xs text-slate-300" data-testid={`bill-none-${x.id}`}>—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-xs">{x.method}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs max-w-xs truncate" title={x.description}>{x.description || '—'}</td>
                    {isMaster && (
                      <td
                        className="px-3 py-2 text-xs whitespace-nowrap"
                        title={enteredBy || 'System / legacy entry'}
                        data-testid={`entered-by-${x.id}`}
                      >
                        {enteredBy ? (
                          <span className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 text-slate-700 rounded-full px-2 py-0.5">
                            {enteredByShort}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">system</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 text-right font-mono text-slate-900">{fmtINR(x.amount)}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => setEditing({ ...x })} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`edit-${x.id}`}>
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => del(x.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`del-${x.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200 font-semibold">
                <tr>
                  <td colSpan={isMaster ? 7 : 6} className="px-3 py-2 text-slate-600 text-right">Total</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-900">{fmtINR(total)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="expense-editor">
          <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <h2 className="text-sm font-semibold">{editing.id ? 'Edit Expense' : 'New Expense'}</h2>
              <button onClick={() => setEditing(null)} className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="px-5 py-4 space-y-3 overflow-y-auto">
              {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-sm" data-testid="editor-error">{err}</div>}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Date</label>
                  <input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} className={inputCls} data-testid="ed-date" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Category *</label>
                  <select value={editing.category} onChange={(e) => {
                    const cat = e.target.value;
                    const CATEGORY_HEAD_MAP = {
                      'Vehicle Maintenance': 'Mechanical',
                      'Raw Materials': 'Consumables',
                    };
                    setEditing((p) => {
                      const next = { ...p, category: cat };
                      if (!p.revenue_head && CATEGORY_HEAD_MAP[cat]) {
                        next.revenue_head = CATEGORY_HEAD_MAP[cat];
                        next.revenue_head_auto = true;
                      } else if (!p.revenue_head && cat) {
                        next.revenue_head = 'Overhead';
                        next.revenue_head_auto = true;
                      }
                      return next;
                    });
                  }} className={inputCls} data-testid="ed-category">
                    <option value="">Select…</option>
                    {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Amount (₹) *</label>
                  <input type="number" min={0} step="0.01" value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: Number(e.target.value || 0) })} className={inputCls} data-testid="ed-amount" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Method</label>
                  <select value={editing.method} onChange={(e) => setEditing({ ...editing, method: e.target.value })} className={inputCls} data-testid="ed-method">
                    {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs text-slate-500">Revenue Head (cost centre)</label>
                    {editing.revenue_head_auto && editing.revenue_head && (
                      <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded" data-testid="ed-auto-tagged">
                        Auto-tagged · review
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5" data-testid="ed-revenue-head-picker">
                    {REVENUE_HEADS.map((h) => (
                      <button key={h} type="button"
                        onClick={() => setEditing({ ...editing, revenue_head: h, revenue_head_auto: false })}
                        className={`text-xs px-2.5 py-1 rounded-full border transition ${editing.revenue_head === h ? 'bg-teal-100 border-teal-400 text-teal-800 font-semibold' : 'bg-white border-slate-300 text-slate-600 hover:border-slate-400'}`}
                        data-testid={`ed-head-${h.replace(/\s+/g, '-').toLowerCase()}`}>
                        {h}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Which service department this spend belongs to. Feeds the Cost-Centre P&amp;L report.
                  </p>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">Paid To</label>
                  <input type="text" value={editing.paid_to || ''} onChange={(e) => onPaidToChange(e.target.value)} className={inputCls} placeholder="Vendor name / staff" data-testid="ed-paid-to" />
                </div>
                <div className="col-span-2 border border-teal-200 bg-teal-50/50 rounded-lg p-3 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-800">
                    <input
                      type="checkbox"
                      checked={!!editing.is_gst_invoice}
                      onChange={(e) => setEditing({ ...editing, is_gst_invoice: e.target.checked })}
                      className="w-4 h-4 accent-teal-600"
                      data-testid="ed-is-gst"
                    />
                    <span className="font-medium">This is a GST invoice</span>
                    <span className="text-xs text-slate-500 font-normal">(audit-only marker)</span>
                  </label>
                  {editing.is_gst_invoice && (
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Supplier GSTIN</label>
                        <input
                          type="text"
                          value={editing.supplier_gstin || ''}
                          onChange={(e) => setEditing({ ...editing, supplier_gstin: (e.target.value || '').toUpperCase().slice(0, 15) })}
                          className={`${inputCls} font-mono uppercase ${editing.supplier_gstin && !GSTIN_RE.test(editing.supplier_gstin) ? 'border-amber-400' : ''}`}
                          placeholder="29AAGCS1234K1Z9"
                          maxLength={15}
                          data-testid="ed-supplier-gstin"
                        />
                        {editing.supplier_gstin && !GSTIN_RE.test(editing.supplier_gstin) && (
                          <p className="text-[11px] text-amber-600 mt-1">GSTIN should be 15 chars: 2 digits + 5 letters + 4 digits + letter + alphanumeric + Z + alphanumeric</p>
                        )}
                      </div>
                      <div className="pt-1 border-t border-teal-200">
                        <p className="text-[11px] text-slate-600 mb-1.5">
                          Tax breakup <span className="text-slate-400">(feeds ITC / GSTR-2 report — auto-filled by OCR, edit if needed)</span>
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <TaxField label="Taxable Value" value={editing.taxable_value}
                            onChange={(v) => setEditing({ ...editing, taxable_value: v })}
                            testId="ed-taxable-value" />
                          <TaxField label="CGST" value={editing.cgst_amount}
                            onChange={(v) => setEditing({ ...editing, cgst_amount: v })}
                            testId="ed-cgst" />
                          <TaxField label="SGST" value={editing.sgst_amount}
                            onChange={(v) => setEditing({ ...editing, sgst_amount: v })}
                            testId="ed-sgst" />
                          <TaxField label="IGST" value={editing.igst_amount}
                            onChange={(v) => setEditing({ ...editing, igst_amount: v })}
                            testId="ed-igst" />
                          <TaxField label="Cess" value={editing.cess_amount}
                            onChange={(v) => setEditing({ ...editing, cess_amount: v })}
                            testId="ed-cess" />
                          <div className="rounded border border-teal-300 bg-teal-100/60 px-2 py-1.5 text-xs">
                            <div className="text-[10px] uppercase text-teal-700 font-semibold">Total ITC</div>
                            <div className="font-semibold text-teal-800" data-testid="ed-total-itc">
                              ₹ {fmtINR(
                                Number(editing.cgst_amount || 0)
                                + Number(editing.sgst_amount || 0)
                                + Number(editing.igst_amount || 0)
                                + Number(editing.cess_amount || 0),
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">Reference / Bill #</label>
                  <input type="text" value={editing.reference || ''} onChange={(e) => setEditing({ ...editing, reference: e.target.value })} className={inputCls} placeholder="Bill no., UPI txn ID, etc." data-testid="ed-reference" />
                </div>
                {dupeMatches.length > 0 && (
                  <div
                    className="col-span-2 border border-amber-300 bg-amber-50 rounded-lg p-3 text-sm"
                    data-testid="ed-duplicate-warning"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5" aria-hidden>⚠</span>
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <p className="font-medium text-amber-800">
                            Possible duplicate bill in current FY
                          </p>
                          <label
                            className="inline-flex items-center gap-1.5 text-[11px] text-amber-800 cursor-pointer bg-white/60 border border-amber-200 rounded px-2 py-0.5"
                            title="When on, only bills with the same amount (± ₹0.50) will be flagged"
                          >
                            <input
                              type="checkbox"
                              checked={dupeMatchAmount}
                              onChange={(e) => setDupeMatchAmount(e.target.checked)}
                              className="w-3.5 h-3.5 accent-amber-600"
                              data-testid="ed-dupe-match-amount"
                            />
                            Also match amount
                          </label>
                        </div>
                        <p className="text-xs text-amber-700 mt-0.5">
                          {dupeMatches.length === 1
                            ? 'A bill with this number and supplier is already recorded:'
                            : `${dupeMatches.length} existing bills match this number & supplier:`}
                        </p>
                        <ul className="mt-1.5 space-y-1">
                          {dupeMatches.map((m) => (
                            <li key={m.id} className="text-xs text-amber-900 flex items-center gap-2 flex-wrap">
                              <span className="font-mono bg-white/70 border border-amber-200 rounded px-1.5 py-0.5">
                                {m.reference || '(no ref)'}
                              </span>
                              <span>{m.date}</span>
                              <span>·</span>
                              <span>{fmtINR(m.amount)}</span>
                              <span>·</span>
                              <span className="italic">{m.paid_to || '—'}</span>
                              {m.category && (
                                <span className="text-amber-700">({m.category})</span>
                              )}
                            </li>
                          ))}
                        </ul>
                        <p className="text-[11px] text-amber-700 mt-1.5">
                          You can still save if this is intentional (e.g., a
                          separate line item on the same bill).
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">Description</label>
                  <textarea rows={2} value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={inputCls} />
                </div>
                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs text-slate-500">Bill / Receipt Photo</label>
                    {extracting && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-teal-700" data-testid="ed-extracting">
                        <Sparkles size={11} className="animate-pulse" /> Reading invoice…
                      </span>
                    )}
                    {!extracting && autofillHint && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 transition-opacity" data-testid="ed-autofill-hint">
                        <Sparkles size={11} /> {autofillHint}
                      </span>
                    )}
                    {!extracting && !autofillHint && (
                      <DriveIQBadge label="Read by" testId="expense-driveiq" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-3 py-2 rounded-lg cursor-pointer inline-flex items-center gap-1.5">
                      <Upload size={14} /> Choose…
                      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => e.target.files?.[0] && uploadAttachment(e.target.files[0])} data-testid="ed-upload" />
                    </label>
                    {editing.attachment_url && (
                      <>
                        <BillThumb
                          url={editing.attachment_url}
                          onOpen={setViewingBill}
                          size="lg"
                          testId="ed-bill-thumb"
                        />
                        <button
                          type="button"
                          onClick={() => setViewingBill(editing.attachment_url)}
                          className="text-xs font-semibold text-teal-700 underline"
                          data-testid="ed-bill-view"
                        >
                          View bill
                        </button>
                        <button
                          onClick={() => setEditing({ ...editing, attachment_url: '' })}
                          className="p-1 rounded hover:bg-red-50 text-red-500"
                          title="Remove this bill"
                          data-testid="ed-bill-remove"
                        >
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">Upload a JPG/PNG/PDF bill and we&apos;ll auto-fill the supplier name + GSTIN.</p>
                </div>
                {!editing.id
                  && !extracting
                  && editing.attachment_url
                  && editing.category
                  && (editing.paid_to || '').trim()
                  && Number(editing.amount) > 0 && (
                  <div className="col-span-2">
                    <button
                      type="button"
                      onClick={save}
                      disabled={saving}
                      className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white font-semibold text-sm px-4 py-2.5 rounded-lg shadow-sm transition"
                      data-testid="ed-quick-save"
                    >
                      {saving
                        ? <><Loader2 className="animate-spin" size={15} /> Saving…</>
                        : <>
                          <Sparkles size={15} /> Save this expense
                          <span className="hidden sm:inline text-emerald-100 font-normal">
                            · ₹ {fmtINR(editing.amount)} · {editing.category}
                          </span>
                        </>
                      }
                    </button>
                    <p className="text-[10.5px] text-slate-500 mt-1 text-center">
                      All required fields were auto-filled from the bill.
                      Tap to save — or scroll for the regular Save.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="text-sm px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-200" data-testid="ed-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="text-sm bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="ed-save">
                {saving ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <BillViewer url={viewingBill} onClose={() => setViewingBill('')} />
    </div>
  );
};

const TaxField = ({ label, value, onChange, testId }) => (
  <div>
    <div className="text-[10px] uppercase text-slate-500 mb-0.5">{label}</div>
    <div className="relative">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹</span>
      <input
        type="number"
        step="0.01"
        min="0"
        value={value || 0}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className={`${inputCls} pl-6 py-1.5 text-sm`}
        data-testid={testId}
      />
    </div>
  </div>
);

export default ExpensesPage;
